import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnimatedIcon } from './AnimatedIcon';
import { TOUR_UI, resolveTourLang } from '../tours/studyTours';
import { ALL_TOURS, TourKey } from '../tours/allTours';
import { scrollTourTarget, tourPopoverStyle } from '../utils/tourPosition';

/**
 * Tour guiado por página (design minimal v1).
 * Botão "?" -> confirmação -> passos com a página escurecida a 60% e só o elemento atual em destaque.
 * Independente do tour de boas-vindas (TourOverlay/tourService). Textos no idioma nativo do usuário.
 */
const ease = [0.22, 1, 0.36, 1] as const;
const PAD = 8;

type Rect = { top: number; left: number; width: number; height: number };

const isVisible = (sel: string) => {
  const el = document.querySelector(sel) as HTMLElement | null;
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

export const PageTourButton: React.FC<{ tourKey: TourKey; className?: string }> = ({ tourKey, className = '' }) => {
  const { i18n } = useTranslation();
  const lang = resolveTourLang(i18n.language);
  const ui = TOUR_UI[lang];
  const [phase, setPhase] = useState<'idle' | 'confirm' | 'running'>('idle');
  const [steps, setSteps] = useState<{ target: string; title: string; text: string }[]>([]);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const start = () => {
    const defs = ALL_TOURS[tourKey].steps
      .filter(s => isVisible(s.target))
      .map(s => ({ target: s.target, title: s.text[lang][0], text: s.text[lang][1] }));
    setSteps(defs);
    setIdx(0);
    setPhase(defs.length ? 'running' : 'idle');
  };
  const exit = useCallback(() => { setPhase('idle'); setRect(null); }, []);
  const next = useCallback(() => setIdx(i => { if (i >= steps.length - 1) { exit(); return i; } return i + 1; }), [steps.length, exit]);
  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);

  // Mede o elemento do passo atual (rola até ele antes)
  const measure = useCallback(() => {
    const step = steps[idx];
    if (!step) return;
    const el = document.querySelector(step.target) as HTMLElement | null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
  }, [steps, idx]);

  useLayoutEffect(() => {
    if (phase !== 'running') return;
    const el = document.querySelector(steps[idx]?.target || '') as HTMLElement | null;
    scrollTourTarget(el);
    const t1 = setTimeout(measure, 380);
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { clearTimeout(t1); window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [phase, idx, steps, measure]);

  useEffect(() => {
    if (phase !== 'running') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exit();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, next, prev, exit]);

  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const counter = `${String(idx + 1).padStart(2, '0')}/${String(steps.length).padStart(2, '0')}`;

  // posição do balão: abaixo do destaque, acima, ou preso ao rodapé; sempre dentro da tela
  const W = typeof window !== 'undefined' ? Math.min(340, window.innerWidth - 32) : 340;
  const pop: React.CSSProperties = typeof window !== 'undefined' ? tourPopoverStyle(rect, W) : { width: W };

  return (
    <>
      <button
        type="button"
        data-tour="tour-button"
        onClick={() => setPhase('confirm')}
        title={ui.help}
        aria-label={ui.help}
        className={`w-10 h-10 rounded-full flex items-center justify-center bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone cursor-pointer ${className}`}
      >
        <AnimatedIcon motion="pop"><HelpCircle size={18} strokeWidth={1.75} /></AnimatedIcon>
      </button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {phase === 'confirm' && (
            <motion.div
              key="tour-confirm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10050] flex items-center justify-center p-5 bg-black/60 backdrop-blur-sm"
              onClick={() => setPhase('idle')}
            >
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.4, ease }}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm rounded-[28px] bg-surface text-ink p-7 text-center border border-black/[0.06] dark:border-white/[0.06]"
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-tint-sky flex items-center justify-center">
                  <AnimatedIcon motion="swing" active><HelpCircle size={24} strokeWidth={1.6} /></AnimatedIcon>
                </div>
                <h3 className="font-display text-2xl font-light tracking-[-0.03em] leading-tight">{ui.ask}</h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">{ui.askText}</p>
                <div className="mt-6 grid grid-cols-2 gap-2">
                  <button onClick={() => setPhase('idle')} className="h-11 rounded-full text-sm font-medium text-muted hover:text-ink bg-transparent border border-black/[0.08] dark:border-white/[0.08] cursor-pointer">{ui.cancel}</button>
                  <button onClick={start} className="h-11 rounded-full text-sm font-medium bg-primary text-slate-900 hover:opacity-90 cursor-pointer">{ui.start}</button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {phase === 'running' && step && (
            <motion.div key="tour-run" className="fixed inset-0 z-[10050]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* bloqueia cliques na página durante o tour */}
              <div className="absolute inset-0" />
              {rect ? (
                <motion.div
                  className="absolute rounded-[18px] pointer-events-none"
                  style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)' }}
                  initial={false}
                  animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
                  transition={{ duration: 0.45, ease }}
                >
                  <span className="absolute inset-0 rounded-[18px] ring-2 ring-white/70" />
                </motion.div>
              ) : (
                <div className="absolute inset-0 bg-black/60" />
              )}

              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease }}
                style={{ position: 'fixed', ...pop }}
                className="rounded-[22px] bg-surface text-ink p-5 border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.5)]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[11px] tracking-[0.16em] text-primary tabular-nums">{counter}</span>
                  <button onClick={exit} className="text-[12px] text-muted hover:text-ink cursor-pointer">{ui.exit}</button>
                </div>
                <h4 className="font-display text-lg font-medium tracking-[-0.02em] leading-snug">{step.title}</h4>
                <p className="mt-1.5 text-sm text-ink/70 leading-relaxed">{step.text}</p>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="flex gap-1">
                    {steps.map((_, i) => (
                      <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-4 bg-ink' : 'w-1.5 bg-ink/15'}`} />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                  {idx > 0 && (
                    <button onClick={prev} aria-label="←" className="w-10 h-10 rounded-full flex items-center justify-center border border-ink/15 text-ink hover:bg-ink/5 cursor-pointer">
                      <ArrowLeft size={15} strokeWidth={1.75} />
                    </button>
                  )}
                  <button onClick={next} className="group h-10 pl-4 pr-1.5 rounded-full bg-primary text-slate-900 text-sm font-medium flex items-center gap-2 cursor-pointer hover:opacity-90">
                    {isLast ? ui.done : ui.next}
                    <span className="w-7 h-7 rounded-full bg-surface/15 flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                      <ArrowRight size={14} strokeWidth={1.75} />
                    </span>
                  </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default PageTourButton;
